import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import {
  X, Bell, ChatCircleText, CalendarPlus, CalendarX, PencilSimpleLine, IdentificationCard, WarningCircle,
  SignIn, SignOut, House, CurrencyEur, Checks, BellRinging,
} from "@phosphor-icons/react";
import api from "@/lib/api";
import { toast } from "sonner";
import RemindersPanel from "@/components/NotificationsPanel";
import { useSwipeDismiss } from "@/components/motion/useSwipeDismiss";
import { enablePush, disablePush, getPushSubscription, pushSupported, isIos, isStandalone, syncPush } from "@/lib/push";

export const KIND = {
  message: { label: "Poruke", icon: ChatCircleText, tone: "bg-sky-100 text-sky-700" },
  reservation_new: { label: "Nove", icon: CalendarPlus, tone: "bg-emerald-100 text-emerald-700" },
  reservation_modified: { label: "Izmjene", icon: PencilSimpleLine, tone: "bg-amber-100 text-amber-700" },
  reservation_cancelled: { label: "Otkazane", icon: CalendarX, tone: "bg-rose-100 text-rose-700" },
  checkin_submitted: { label: "Check-in", icon: IdentificationCard, tone: "bg-violet-100 text-violet-700" },
  sync_error: { label: "OTA greške", icon: WarningCircle, tone: "bg-red-100 text-red-700" },
};

const linkFor = (n) => (n.thread_id ? `/inbox?thread=${n.thread_id}` : n.kind === "sync_error" ? "/channels" : n.reservation_id ? `/reservations?open=${n.reservation_id}` : "/dashboard");

const timeAgo = (iso) => {
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "upravo";
  if (s < 3600) return `prije ${Math.floor(s / 60)} min`;
  if (s < 86400) return `prije ${Math.floor(s / 3600)} h`;
  return new Date(iso).toLocaleDateString("hr-HR", { day: "numeric", month: "numeric" });
};

/** Polls the feed; new items → live toast + browser notification. */
export function useNotificationFeed() {
  const [unread, setUnread] = useState(0);
  const [toasts, setToasts] = useState([]);
  const [pulse, setPulse] = useState(false);
  const lastTs = useRef(null);
  const inFlight = useRef(false);
  const seen = useRef(new Set());
  const poll = useCallback(async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    const since = lastTs.current;
    try {
      const { data } = await api.get("/notifications", { params: since ? { since, limit: 20 } : { limit: 1 } });
      setUnread(data.unread || 0);
      const items = data.items || [];
      if (since === null) {
        // first load only initialises the cursor — never toasts existing items
        if (lastTs.current === null) lastTs.current = items[0]?.created_at || "1970-01-01T00:00:00+00:00";
        items.forEach((n) => seen.current.add(n.id));
        return;
      }
      const fresh = items.filter((n) => !seen.current.has(n.id) && !n.read && n.created_at > since);
      if (items.length) lastTs.current = items[0].created_at > lastTs.current ? items[0].created_at : lastTs.current;
      items.forEach((n) => seen.current.add(n.id));
      if (!fresh.length) return;
      fresh.splice(3);
      fresh.reverse();
      setToasts((t) => [...t, ...fresh].slice(-4));
      setPulse(true);
      const pushed = await getPushSubscription().catch(() => null);
      if (!pushed && typeof Notification !== "undefined" && Notification.permission === "granted") {
        fresh.forEach((n) => {
          try {
            const bn = new Notification(n.title, { body: n.body, tag: n.id, icon: "/favicon.ico" });
            bn.onclick = () => { window.focus(); window.location.assign(linkFor(n)); };
          } catch { /* ignore */ }
        });
      }
    } catch { /* offline / logged out */ } finally { inFlight.current = false; }
  }, []);
  useEffect(() => {
    poll();
    const t = setInterval(poll, 20000);
    const onFocus = () => poll();
    window.addEventListener("focus", onFocus);
    return () => { clearInterval(t); window.removeEventListener("focus", onFocus); };
  }, [poll]);
  useEffect(() => {
    if (!pulse) return;
    const t = setTimeout(() => setPulse(false), 4500);
    return () => clearTimeout(t);
  }, [pulse]);
  const dismiss = (id) => setToasts((t) => t.filter((x) => x.id !== id));
  return { unread, setUnread, toasts, dismiss, pulse, setPulse, refresh: poll };
}

function LiveToast({ n, onDone }) {
  const navigate = useNavigate();
  const [leaving, setLeaving] = useState(false);
  const meta = KIND[n.kind] || KIND.message;
  const Icon = meta.icon;
  const minimize = () => { setLeaving(true); setTimeout(onDone, 420); };
  const swipeRef = useSwipeDismiss({ onClose: onDone, directions: ["right", "up", "left"] });
  useEffect(() => {
    const a = setTimeout(() => setLeaving(true), 6000);
    const b = setTimeout(onDone, 6450);
    return () => { clearTimeout(a); clearTimeout(b); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div ref={swipeRef} data-swipe-fade="1" className={`rw-live-toast pointer-events-auto relative w-[320px] max-w-[calc(100vw-24px)] touch-pan-y ${leaving ? "rw-live-toast-out" : ""}`} data-testid={`live-toast-${n.kind}`}>
      <button
        type="button"
        onClick={() => { api.post("/notifications/read", { ids: [n.id] }).catch(() => {}); navigate(linkFor(n)); onDone(); }}
        className="flex w-full items-start gap-3 rounded-xl border border-border bg-card p-3 pr-9 text-left shadow-xl"
        data-testid={`live-toast-open-${n.id}`}
      >
        <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${meta.tone}`}><Icon size={15} weight="bold" /></span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-semibold text-foreground">{n.title}</span>
          {n.body && <span className="mt-0.5 line-clamp-2 block text-xs text-muted-foreground">{n.body}</span>}
        </span>
      </button>
      <button type="button" onClick={minimize} aria-label="Zatvori obavijest" title="Spremi u obavijesti" className="absolute right-1.5 top-1.5 rounded-full p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground" data-testid={`live-toast-close-${n.id}`}>
        <X size={13} weight="bold" />
      </button>
    </div>
  );
}

export function LiveToasts({ toasts, dismiss, hidden }) {
  if (hidden) return null;
  return createPortal(
    <div className="pointer-events-none fixed right-3 top-[60px] z-[95] flex flex-col items-end gap-2" data-testid="live-toasts">
      {toasts.map((n) => <LiveToast key={n.id} n={n} onDone={() => dismiss(n.id)} />)}
    </div>,
    document.body,
  );
}

function SummaryTiles({ s, go }) {
  if (!s) return <div className="grid grid-cols-3 gap-2">{Array.from({ length: 6 }).map((_, i) => <div key={i} className="rw-skeleton h-16 rounded-lg" />)}</div>;
  const tiles = [
    { k: "arrivals", label: "Dolasci danas", icon: SignIn, to: "/calendar", v: s.arrivals },
    { k: "departures", label: "Odlasci danas", icon: SignOut, to: "/calendar", v: s.departures },
    { k: "in_house", label: "U smještaju", icon: House, to: "/calendar", v: s.in_house },
    { k: "unread_messages", label: "Nepročitane poruke", icon: ChatCircleText, to: "/inbox", v: s.unread_messages, warn: s.unread_messages > 0 },
    { k: "pending_checkin", label: "Čeka check-in", icon: IdentificationCard, to: "/check-in", v: s.pending_checkin, warn: s.pending_checkin > 0 },
    { k: "unpaid", label: "Neplaćeno (u boravku)", icon: CurrencyEur, to: "/reservations", v: s.unpaid, warn: s.unpaid > 0 },
  ];
  return (
    <div className="grid grid-cols-3 gap-2" data-testid="notif-summary">
      {tiles.map((t) => (
        <button key={t.k} type="button" onClick={() => go(t.to)} className={`rounded-lg border p-2 text-left transition-colors hover:bg-muted ${t.warn ? "border-amber-300 bg-amber-50/60" : "border-border bg-card"}`} data-testid={`notif-tile-${t.k}`}>
          <t.icon size={14} className={t.warn ? "text-amber-600" : "text-muted-foreground"} />
          <p className="mt-1 font-mono text-lg font-bold leading-none text-foreground">{t.v}</p>
          <p className="mt-1 truncate text-[10px] text-muted-foreground">{t.label}</p>
        </button>
      ))}
    </div>
  );
}

function FeedRow({ n, go, markOne }) {
  const meta = KIND[n.kind] || KIND.message;
  const Icon = meta.icon;
  return (
    <li>
      <button type="button" onClick={() => { markOne(n.id); go(linkFor(n)); }} className={`flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-muted/40 ${n.read ? "" : "bg-primary/[0.04]"}`} data-testid={`notif-item-${n.id}`}>
        <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${meta.tone}`}><Icon size={14} weight="bold" /></span>
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-2">
            <span className={`min-w-0 flex-1 truncate text-sm ${n.read ? "font-medium" : "font-semibold"} text-foreground`}>{n.title}</span>
            {!n.read && <span className="h-2 w-2 shrink-0 rounded-full bg-primary" />}
          </span>
          {n.body && <span className="mt-0.5 line-clamp-2 block text-xs text-muted-foreground">{n.body}</span>}
          <span className="mt-0.5 block text-[10px] text-muted-foreground/80">{timeAgo(n.created_at)}</span>
        </span>
      </button>
    </li>
  );
}

function PushToggle() {
  const [state, setState] = useState("loading");
  const [busy, setBusy] = useState(false);
  useEffect(() => { getPushSubscription().then((s) => setState(s ? "on" : "off")).catch(() => setState("off")); }, []);
  const sendTest = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/push/test");
      if (data.total === 0) toast.error("Server nema nijedan prijavljeni uređaj — klikni Isključi pa Uključi.");
      else if (data.errors?.length) toast.error(`Poslano ${data.sent}/${data.total}. Greška: ${data.errors[0]}`);
      else toast.success(`Testna obavijest poslana na ${data.sent} ${data.sent === 1 ? "uređaj" : "uređaja"}. Ako ne stigne, provjeri Postavke → Aplikacije → Chrome → Obavijesti.`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Slanje nije uspjelo"); } finally { setBusy(false); }
  };
  const needsInstall = !pushSupported() && isIos() && !isStandalone();
  const toggle = async () => {
    setBusy(true);
    try {
      if (state === "on") { await disablePush(); setState("off"); toast.success("Push obavijesti isključene na ovom uređaju"); }
      else { await enablePush(); setState("on"); toast.success("Push obavijesti uključene na ovom uređaju"); setTimeout(sendTest, 300); }
    } catch (e) { toast.error(e.message || "Uključivanje nije uspjelo"); } finally { setBusy(false); }
  };
  if (state === "loading") return null;
  return (
    <div className="mt-3 flex items-center gap-3 rounded-lg border border-border bg-background p-2.5" data-testid="push-toggle">
      <BellRinging size={18} className={state === "on" ? "text-emerald-600" : "text-muted-foreground"} />
      <div className="min-w-0 flex-1">
        <p className="text-xs font-semibold text-foreground">Push obavijesti na ovom uređaju</p>
        <p className="text-[11px] leading-snug text-muted-foreground">
          {needsInstall ? "iPhone: Safari → Podijeli → Dodaj na početni zaslon, zatim otvori Lodgy s početnog zaslona." : state === "on" ? "Uključeno — stižu i kad je aplikacija zatvorena." : "Primaj obavijesti i kad je aplikacija zatvorena ili je zaslon zaključan."}
        </p>
      </div>
      {!needsInstall && state === "on" && (
        <button type="button" onClick={sendTest} disabled={busy} className="shrink-0 rounded-md border border-border px-2.5 py-1.5 text-[11px] font-semibold text-foreground hover:bg-muted disabled:opacity-50" data-testid="push-test-btn">Test</button>
      )}
      {!needsInstall && (
        <button type="button" onClick={toggle} disabled={busy} className={`shrink-0 rounded-md px-2.5 py-1.5 text-[11px] font-semibold disabled:opacity-50 ${state === "on" ? "border border-border text-foreground hover:bg-muted" : "bg-primary text-primary-foreground"}`} data-testid="push-toggle-btn">
          {busy ? "..." : state === "on" ? "Isključi" : "Uključi"}
        </button>
      )}
    </div>
  );
}

function groupLabel(iso) {
  const d = new Date(iso); const t = new Date();
  const diff = Math.floor((new Date(t.toDateString()) - new Date(d.toDateString())) / 86400000);
  return diff === 0 ? "Danas" : diff === 1 ? "Jučer" : "Ranije";
}

export function NotificationDrawer({ open, onClose, onRead }) {
  const navigate = useNavigate();
  const [tab, setTab] = useState("feed");
  const [items, setItems] = useState(null);
  const [summary, setSummary] = useState(null);
  const [kind, setKind] = useState("");
  const backdropRef = useRef(null);
  const panelRef = useSwipeDismiss({ onClose, directions: ["right"], enabled: open, backdropRef });
  useEffect(() => {
    if (!open) return;
    api.get("/notifications", { params: { limit: 100 } }).then(({ data }) => setItems(data.items || [])).catch(() => setItems([]));
    api.get("/notifications/summary").then(({ data }) => setSummary(data)).catch(() => {});
  }, [open]);
  if (!open) return null;
  const go = (to) => { onClose(); navigate(to); };
  const markOne = (id) => api.post("/notifications/read", { ids: [id] }).then(onRead).catch(() => {});
  const markAll = async () => { await api.post("/notifications/read", { all: true }); setItems((xs) => (xs || []).map((x) => ({ ...x, read: true }))); onRead(); };
  const visible = (items || []).filter((n) => !kind || n.kind === kind);
  const groups = visible.reduce((acc, n) => { const g = groupLabel(n.created_at); (acc[g] ||= []).push(n); return acc; }, {});
  return createPortal(
    <div className="fixed inset-0 z-[70]" data-testid="notifications-panel">
      <div ref={backdropRef} className="absolute inset-0 bg-black/30" onClick={onClose} />
      <aside ref={panelRef} className="rw-slide-right absolute right-0 top-0 flex h-full w-full max-w-[440px] flex-col overflow-hidden bg-background shadow-2xl">
        <div className="flex items-center justify-between border-b border-border bg-card px-4 py-3">
          <h2 className="flex items-center gap-2 text-base font-semibold"><Bell size={18} weight="fill" className="text-primary" /> Obavijesti</h2>
          <div className="flex items-center gap-1">
            <button onClick={onClose} className="rounded-full p-1.5 text-muted-foreground hover:bg-muted" data-testid="notifications-close"><X size={16} weight="bold" /></button>
          </div>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto">
          <div className="border-b border-border bg-card p-4"><SummaryTiles s={summary} go={go} /><PushToggle /></div>
          <div className="sticky top-0 z-10 flex items-center gap-1 border-b border-border bg-background/95 px-4 py-2 backdrop-blur">
            {[{ id: "feed", l: "Aktivnost" }, { id: "reminders", l: "Podsjetnici" }].map((t) => (
              <button key={t.id} type="button" onClick={() => setTab(t.id)} className={`rounded-md px-3 py-1 text-xs font-semibold ${tab === t.id ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"}`} data-testid={`notif-tab-${t.id}`}>{t.l}</button>
            ))}
            {tab === "feed" && <button type="button" onClick={markAll} className="ml-auto inline-flex items-center gap-1 text-[11px] font-medium text-primary hover:underline" data-testid="notif-mark-all"><Checks size={13} /> Označi sve pročitano</button>}
          </div>
          {tab === "feed" ? (
            <>
              <div className="flex flex-wrap gap-1.5 px-4 py-2" data-testid="notif-kind-filters">
                <button type="button" onClick={() => setKind("")} className={`shrink-0 rounded-full px-2.5 py-1 text-[11px] font-semibold ${!kind ? "bg-foreground text-background" : "bg-muted text-muted-foreground"}`}>Sve</button>
                {Object.entries(KIND).map(([k, m]) => (
                  <button key={k} type="button" onClick={() => setKind(k === kind ? "" : k)} className={`shrink-0 rounded-full px-2.5 py-1 text-[11px] font-semibold ${kind === k ? "bg-foreground text-background" : "bg-muted text-muted-foreground"}`} data-testid={`notif-kind-${k}`}>{m.label}</button>
                ))}
              </div>
              {items === null ? <div className="space-y-2 p-4">{[0, 1, 2].map((i) => <div key={i} className="rw-skeleton h-14 rounded-md" />)}</div> : visible.length === 0 ? (
                <div className="flex flex-col items-center px-6 py-14 text-center"><Bell size={40} weight="thin" className="text-muted-foreground/40" /><p className="mt-2 text-sm text-muted-foreground">Nema obavijesti</p></div>
              ) : Object.entries(groups).map(([g, list]) => (
                <div key={g}>
                  <p className="px-4 pb-1 pt-3 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{g}</p>
                  <ul className="divide-y divide-border/60">{list.map((n) => <FeedRow key={n.id} n={n} go={go} markOne={markOne} />)}</ul>
                </div>
              ))}
            </>
          ) : <RemindersPanel open embedded onClose={onClose} />}
        </div>
      </aside>
    </div>,
    document.body,
  );
}
